import {Button, Dropdown, Icon, MenuListItem, NavItem} from '@albert-io/atomic';
import React, {ComponentProps, useState} from 'react';
import {Link} from 'react-router';

import './global-navigation.scss';
import classNames from 'classnames';

type AssignmentsFoldersDropdownProps = {
  linkClasses?: string;
  link: any; // Assuming 'link' is an Immutable.js Map or similar structure
  onMenuItemClick?: () => void;
  isForDrawer?: boolean;
  isMatchingRoute?: boolean;
  pathname: string;
} & Omit<ComponentProps<typeof NavItem>, 'children'>;

const MobileAssignmentsFoldersDropdown = ({
  linkClasses,
  link,
  onMenuItemClick,
  ...props
}: AssignmentsFoldersDropdownProps) => {
  const [assignmentsDropdownOpen, setAssignmentsDropdownOpen] = useState(false);

  return (
    <>
      <Button
        className={`${linkClasses} u-width_100 global-nav-drawer__assignments-dropdown`}
        onClick={() => setAssignmentsDropdownOpen(!assignmentsDropdownOpen)}
      >
        {link.get('name')}
        <Icon icon={assignmentsDropdownOpen ? 'caret-up' : 'caret-down'} className='u-mar-l_1' />
      </Button>
      {assignmentsDropdownOpen && (
        <>
          <Link
            to='/assignments'
            className={classNames(`${linkClasses} global-nav-drawer__assignments-subitem`, {
              'global-nav-drawer__selected': props.pathname?.startsWith('/assignments')
            })}
            onClick={(e) => {
              e.stopPropagation();
              if (onMenuItemClick) {
                onMenuItemClick();
              }
            }}
          >
            Assignments
          </Link>
          <Link
            to='/saved'
            className={classNames(`${linkClasses} global-nav-drawer__assignments-subitem`, {
              'global-nav-drawer__selected': props.pathname?.startsWith('/saved')
            })}
            onClick={(e) => {
              e.stopPropagation();
              if (onMenuItemClick) {
                onMenuItemClick();
              }
            }}
          >
            Folders
          </Link>
        </>
      )}
    </>
  );
};

const NonMobileAssignmentsFoldersDropdown = ({
  link,
  onMenuItemClick,
  isMatchingRoute = false,
  pathname,
  ...props
}: AssignmentsFoldersDropdownProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // The dropdown trigger will handle opening the dropdown
      (event.currentTarget as HTMLElement).click();
    }
  };

  return (
    <Dropdown
      trigger={
        <span>
          <li key={link.get('key')}>
            <NavItem
              {...props}
              as='div'
              icon={link.get('icon', null)}
              active={isMatchingRoute}
              data-testid='assignments_dropdown-btn'
              {...({
                tabIndex: 0,
                role: 'button',
                'aria-expanded': 'false',
                'aria-haspopup': 'true',
                onKeyDown: handleKeyDown
              } as any)}
            >
              {link.get('name')}
              <Icon icon='caret-down' className='u-mar-l_1' />
            </NavItem>
          </li>
        </span>
      }
      position='bottom-start'
      closeOnItemClicked
      trayProps={{
        modifiers: {
          preventOverflow: {
            padding: 0
          }
        }
      }}
    >
      <div className='u-pad_1'>
        <MenuListItem
          as='a'
          href='/assignment-builder/create-assignment'
          className='nav-submenu_item nav-submenu_blue-item'
          onMenuItemClick={onMenuItemClick}
          data-testid='assignments_new-assignment-btn'
        >
          <Icon icon='plus' className='u-mar-r_1' />
          New Assignment
        </MenuListItem>
        <div className='nav-submenu_border-line u-mar-tb_1' />
        <MenuListItem
          as={Link}
          to='/assignments'
          className='nav-submenu_item'
          onMenuItemClick={onMenuItemClick}
          selected={pathname?.startsWith('/assignments')}
          data-testid='nav-assignments-link'
          disableWhenSelected={false}
        >
          <Icon icon={['fal', 'file-alt']} className='nav-submenu_assignments-icon' />
          Assignments
        </MenuListItem>
        <MenuListItem
          as={Link}
          to='/saved'
          className='nav-submenu_item'
          onMenuItemClick={onMenuItemClick}
          selected={pathname?.startsWith('/saved')}
          data-testid='nav-folders-link'
          disableWhenSelected={false}
        >
          <Icon icon={['fal', 'folder-open']} className='u-mar-r_1' />
          Folders
        </MenuListItem>
      </div>
    </Dropdown>
  );
};

const AssignmentsFoldersDropdown = (props: AssignmentsFoldersDropdownProps) => {
  const {isForDrawer} = props;

  if (isForDrawer) {
    return <MobileAssignmentsFoldersDropdown {...props} />;
  }

  return <NonMobileAssignmentsFoldersDropdown {...props} />;
};

export default AssignmentsFoldersDropdown;
